import { defineStore } from 'pinia'
import _ from 'lodash'
import JSZip from 'jszip'
import { BlockBlobClient } from '@azure/storage-blob'

import { resourceApi } from './common/api'
import { rules } from './common/commonFunctions'
import { createIndexedDBAdapter } from './common/indexedDbStorage'

const resourceCategoriesApi = resourceApi.extend('resourceCategories')
const resourceDataApi = resourceApi.extend('resourceData')
const resourceFilesApi = resourceApi.extend('resourceFiles')
const simulationResourcesApi = resourceApi.extend('simulationResources')
const blobApi = resourceApi.extend('blob')

const filesIndexedDb = createIndexedDBAdapter('resource', 'files')
const downloadsIndexedDb = createIndexedDBAdapter('download', 'jobs')

const hasPrivateResources = (activeResources) => {
  return activeResources == null || activeResources.length == 0
    ? 0
    : activeResources.filter((r) => r.institutionId != null).length > 0
}

export const useResource = defineStore('resource', {
  persist: [
    {
      key: (context) => `${__APP_NAME__}_${context}`,
      pick: [
        'leftWidth',
        'leftMinWidth',
        'leftMaxWidth',
        'leftParentWidth',
        'categoryActive',
        'downloadJobs',
        'downloadIds'
      ],
      storage: localStorage
    }
  ],

  state() {
    return {
      rules: rules.resource,
      rulesCategory: rules.resourceCategory,
      leftMinWidthPercent: 0.15,
      leftMaxWidthPercent: 0.25,
      leftWidthPercent: 0.2,
      leftWidth: null,
      leftMinWidth: null,
      leftMaxWidth: null,
      leftParentWidth: null,
      isLoadingActiveCategories: false,
      errorActiveCategories: null,
      statusActiveCategories: null,
      isLoadingArchivingCategories: false,
      errorArchivingCategories: null,
      statusArchivingCategories: null,
      isLoadingEditingCategory: false,
      errorEditingCategory: null,
      statusEditingCategory: null,
      isLoadingCreatingCategory: false,
      errorCreatingCategory: null,
      statusCreatingCategory: null,
      isLoadingActiveResources: false,
      errorActiveResources: null,
      statusActiveResources: null,
      isLoadingArchivedResources: false,
      errorArchivedResources: null,
      statusArchivedResources: null,
      isLoadingArchivingResources: false,
      errorArchivingResources: null,
      statusArchivingResources: null,
      isLoadingRestoringResources: false,
      errorRestoringResources: null,
      statusRestoringResources: null,
      isLoadingPurgingResources: false,
      errorPurgingResources: null,
      statusPurgingResources: null,
      isLoadingMovingResources: false,
      errorMovingResources: null,
      statusMovingResources: null,
      isLoadingEditingResource: false,
      errorEditingResource: null,
      statusEditingResource: null,
      isLoadingCreatingResource: false,
      errorCreatingResource: null,
      statusCreatingResource: null,
      isLoadingAssignResourcesToSimulation: false,
      errorAssignResourcesToSimulation: null,
      statusAssignResourcesToSimulation: null,
      isLoadingRemoveResourcesFromSimulation: false,
      errorRemoveResourcesFromSimulation: null,
      statusRemoveResourcesFromSimulation: null,
      errorDownloadingFiles: null,
      downloadJobs: {},
      downloadIds: {},
      isActionMultiple: {},
      fetchActiveCategoriesInterval: null,
      fetchActiveResourcesInterval: null,
      fetchArchivedResourcesInterval: null,
      fetchResourcesFrequency: 300000,
      activeCategories: null,
      activeResources: null,
      archivedResources: null,
      categoryActive: ['all'],
      fileArchives: {},
      fileLoaders: {},
      showResourceConfirm: true,
      viewResource: null,
      allCategories: null,
      queryCategory: null,
      editCategory: null,
      categoriesDivider: [3],
      isEdit: false,
      showResource: false,
      showConfirm: false,
      showCategory: false,
      selectedResourceIds: [],
      resourceAction: null,
      categoryAction: null,
      isArchiveCategoriesAndResources: false,
      globalUploadProgress: null,
      simulationResources: null,
      updateSimulationResources: null
    }
  },

  getters: {
    getResourceData: ({
      activeCategories,
      activeResources,
      archivedResources,
      categoryActive,
      categoriesDivider
    }) => {
      const hasUncategorized =
        categoriesDivider.length > 1 &&
        (((archivedResources == null || archivedResources.length == 0) &&
          categoriesDivider.at(-1) == 4) ||
          (archivedResources != null &&
            archivedResources.length > 0 &&
            categoriesDivider.at(-1) == 5))
      const getTree = (categories) => {
        if (categories == null) return null
        const _categories = [
          ...[
            {
              id: 'all',
              icon: 'mdi-set-all',
              userId: null,
              institutionId: null,
              categoryId: null,
              name: 'ALL',
              container: 'resources',
              institution: null,
              userData: null
            },
            {
              id: 'public',
              icon: 'mdi-earth',
              userId: null,
              institutionId: null,
              categoryId: null,
              name: 'Public',
              container: 'resources',
              institution: null,
              userData: null
            },
            {
              id: 'private',
              icon: 'mdi-school',
              userId: null,
              institutionId: null,
              categoryId: null,
              name: 'Private',
              container: 'resources',
              institution: null,
              userData: null
            }
          ],
          ...categories
        ]
        const _hasPrivateResources = hasPrivateResources(activeResources)
        if (!_hasPrivateResources)
          _.remove(_categories, (r) => r.id == 'private')
        let spliceIndex = _hasPrivateResources ? 3 : 2
        if (archivedResources?.length) {
          _categories.splice(spliceIndex, 0, {
            id: 'archived',
            icon: 'mdi-package-down',
            color: 'red',
            userId: null,
            institutionId: null,
            categoryId: null,
            name: 'Archived',
            container: 'resources',
            institution: null,
            userData: null
          })
          spliceIndex++
        }
        if (hasUncategorized)
          _categories.splice(spliceIndex, 0, {
            id: 'uncategorized',
            icon: 'mdi-folder-question',
            color: 'teal',
            userId: null,
            institutionId: null,
            categoryId: null,
            name: 'Uncategorized',
            container: 'resources',
            institution: null,
            userData: null
          })
        const map = new Map(_categories.map((item) => [item.id, { ...item }]))

        const roots = []

        map.forEach((node) => {
          if (node.categoryId) {
            const parent = map.get(node.categoryId)
            if (parent) {
              if (!parent.children) parent.children = []
              parent.children.push(node)
            } else roots.push(node)
          } else roots.push(node)
        })
        return roots
      }
      const categories = _.transform(
        activeCategories || [],
        (result, item, index) => (result[item.id] = { ...item, index }),
        {}
      )
      return {
        tree: getTree(activeCategories, activeResources),
        categories,
        data:
          categoryActive[0] == 'archived' && archivedResources?.length
            ? archivedResources
            : activeResources?.length
              ? _.filter(
                  activeResources,
                  (r) =>
                    categoryActive.includes('all') ||
                    (categoryActive.includes('public') &&
                      r.institutionId == null) ||
                    (categoryActive.includes('private') &&
                      r.institutionId != null) ||
                    (categoryActive.includes('uncategorized') &&
                      r.categoryId == null) ||
                    categoryActive.includes(r.categoryId)
                )
              : null
      }
    },
    getLeftWidth: ({
      leftMinWidthPercent,
      leftMaxWidthPercent,
      leftWidthPercent,
      leftWidth,
      leftMinWidth,
      leftMaxWidth,
      leftParentWidth
    }) => {
      return {
        minWidthPercent: leftMinWidthPercent,
        maxWidthPercent: leftMaxWidthPercent,
        widthPercent: leftWidthPercent,
        width: leftWidth,
        minWidth: leftMinWidth,
        maxWidth: leftMaxWidth,
        parentWidth: leftParentWidth
      }
    },
    getIsLoadingMessage: ({
      isLoadingArchivingCategories,
      isLoadingEditingCategory,
      isLoadingCreatingCategory,
      isLoadingArchivingResources,
      isLoadingRestoringResources,
      isLoadingPurgingResources,
      isLoadingMovingResources,
      isLoadingEditingResource,
      isLoadingCreatingResource,
      isLoadingAssignResourcesToSimulation,
      isLoadingRemoveResourcesFromSimulation,
      isActionMultiple,
      categoryActive
    }) => {
      if (isLoadingArchivingCategories)
        return _.isObjectLike(isActionMultiple) &&
          !_.isEmpty(isActionMultiple) &&
          _.has(isActionMultiple, 'archivingCategories') &&
          isActionMultiple.archivingCategories == true
          ? 'Archiving categories...'
          : 'Archiving category...'
      else if (isLoadingEditingCategory) return 'Updating category...'
      else if (isLoadingCreatingCategory) return 'Creating category...'
      else if (isLoadingArchivingResources)
        return _.isObjectLike(isActionMultiple) &&
          !_.isEmpty(isActionMultiple) &&
          _.has(isActionMultiple, 'archivingResources') &&
          isActionMultiple.archivingResources == true
          ? 'Archiving resources...'
          : 'Archiving resource...'
      else if (isLoadingRestoringResources)
        return _.isObjectLike(isActionMultiple) &&
          !_.isEmpty(isActionMultiple) &&
          _.has(isActionMultiple, 'restoringResources') &&
          isActionMultiple.restoringResources == true
          ? 'Restoring resources...'
          : 'Restoring resource...'
      else if (isLoadingPurgingResources)
        return _.isObjectLike(isActionMultiple) &&
          !_.isEmpty(isActionMultiple) &&
          _.has(isActionMultiple, 'purgingResources') &&
          isActionMultiple.purgingResources == true
          ? 'Purging resources...'
          : 'Purging resource...'
      else if (isLoadingMovingResources)
        return _.isObjectLike(isActionMultiple) &&
          !_.isEmpty(isActionMultiple) &&
          _.has(isActionMultiple, 'movingResources') &&
          isActionMultiple.movingResources == true
          ? `${categoryActive.length == 1 && categoryActive[0] == 'uncategorized' ? 'Setting category' : 'Moving'} resources...`
          : `${categoryActive.length == 1 && categoryActive[0] == 'uncategorized' ? 'Setting category' : 'Moving'} resource...`
      else if (isLoadingEditingResource) return 'Updating resource...'
      else if (isLoadingCreatingResource) return 'Creating resource...'
      else if (isLoadingAssignResourcesToSimulation)
        return _.isObjectLike(isActionMultiple) &&
          !_.isEmpty(isActionMultiple) &&
          _.has(isActionMultiple, 'assignResourcesToSimulation') &&
          isActionMultiple.assignResourcesToSimulation == true
          ? 'Assignining resources to simulation...'
          : 'Assignining resource to simulation...'
      else if (isLoadingRemoveResourcesFromSimulation)
        return _.isObjectLike(isActionMultiple) &&
          !_.isEmpty(isActionMultiple) &&
          _.has(isActionMultiple, 'removeResourcesFromSimulation') &&
          isActionMultiple.removeResourcesFromSimulation == true
          ? 'Removing resources from simulation...'
          : 'Removing resource from simulation...'
      else return null
    },
    getErrors: ({
      errorActiveCategories,
      errorArchivingCategories,
      errorEditingCategory,
      errorCreatingCategory,
      errorActiveResources,
      errorArchivedResources,
      errorArchivingResources,
      errorRestoringResources,
      erroreditingResource,
      errorCreatingResource,
      errorAssignResourcesToSimulation,
      errorRemoveResourcesFromSimulation,
      errorDownloadingFiles
    }) => {
      let returnValue = []
      if (errorActiveCategories)
        returnValue.push({
          text: errorActiveCategories,
          key: 'activeCategories'
        })
      if (errorArchivingCategories)
        returnValue.push({
          text: errorArchivingCategories,
          key: 'archivingCategories'
        })
      if (errorEditingCategory)
        returnValue.push({
          text: errorEditingCategory,
          key: 'editingCategory'
        })
      if (errorCreatingCategory)
        returnValue.push({
          text: errorCreatingCategory,
          key: 'creatingCategory'
        })
      if (errorActiveResources)
        returnValue.push({
          text: errorActiveResources,
          key: 'activeResources'
        })
      if (errorArchivedResources)
        returnValue.push({
          text: errorArchivedResources,
          key: 'archivedResources'
        })
      if (errorArchivingResources)
        returnValue.push({
          text: errorArchivingResources,
          key: 'archivingResources'
        })
      if (errorRestoringResources)
        returnValue.push({
          text: errorRestoringResources,
          key: 'restoringResources'
        })
      if (erroreditingResource)
        returnValue.push({
          text: erroreditingResource,
          key: 'editingResource'
        })
      if (errorCreatingResource)
        returnValue.push({
          text: errorCreatingResource,
          key: 'creatingResource'
        })
      if (errorAssignResourcesToSimulation)
        returnValue.push({
          text: errorAssignResourcesToSimulation,
          key: 'assignResourcesToSimulation'
        })
      if (errorRemoveResourcesFromSimulation)
        returnValue.push({
          text: errorRemoveResourcesFromSimulation,
          key: 'removeResourcesFromSimulation'
        })
      if (errorDownloadingFiles)
        returnValue.push({
          text: errorDownloadingFiles,
          key: 'downloadingFiles'
        })
      return returnValue
    },
    getIsLoadingActiveCategories: ({ isLoadingActiveCategories }) => {
      return isLoadingActiveCategories
    },
    getIsLoadingActiveResources: ({ isLoadingActiveResources }) => {
      return isLoadingActiveResources
    },
    getIsLoadingArchivedResources: ({ isLoadingArchivedResources }) => {
      return isLoadingArchivedResources
    },
    getIsLoadingCreatingResource: ({ isLoadingCreatingResource }) => {
      return isLoadingCreatingResource
    },
    getIsLoadingArchivingResources: ({ isLoadingArchivingResources }) => {
      return isLoadingArchivingResources
    },
    getIsLoadingRestoringResources: ({ isLoadingRestoringResources }) => {
      return isLoadingRestoringResources
    },
    getIsLoadingPurgingResources: ({ isLoadingPurgingResources }) => {
      return isLoadingPurgingResources
    },
    getIsLoadingMovingResources: ({ isLoadingMovingResources }) => {
      return isLoadingMovingResources
    },
    getIsLoadingEditingResource: ({ isLoadingEditingResource }) => {
      return isLoadingEditingResource
    },
    getIsLoadingCreatingCategory: ({ isLoadingCreatingCategory }) => {
      return isLoadingCreatingCategory
    },
    getIsLoadingEditingCategory: ({ isLoadingEditingCategory }) => {
      return isLoadingEditingCategory
    },
    getIsLoadingArchivingCategories: ({ isLoadingArchivingCategories }) => {
      return isLoadingArchivingCategories
    },
    getIsLoadingAssignResourcesToSimulation: ({
      isLoadingAssignResourcesToSimulation
    }) => {
      return isLoadingAssignResourcesToSimulation
    },
    getIsLoadingRemoveResourcesFromSimulation: ({
      isLoadingRemoveResourcesFromSimulation
    }) => {
      return isLoadingRemoveResourcesFromSimulation
    },
    getDownloadProgressByJobId:
      ({ downloadJobs }) =>
      (jobId) =>
        downloadJobs?.[jobId]?.progress || null,
    getDownloadJobById:
      ({ downloadIds }) =>
      (id) =>
        downloadIds?.[id] || null,
    getFetchResourcesFrequency: ({ fetchResourcesFrequency }) => {
      return fetchResourcesFrequency
    },
    getFetchActiveCategoriesInterval: ({ fetchActiveCategoriesInterval }) => {
      return fetchActiveCategoriesInterval
    },
    getFetchActiveResourcesInterval: ({ fetchActiveResourcesInterval }) => {
      return fetchActiveResourcesInterval
    },
    getFetchArchivedResourcesInterval: ({ fetchArchivedResourcesInterval }) => {
      return fetchArchivedResourcesInterval
    },
    getViewResource: ({ viewResource }) => {
      return viewResource
    },
    getShowResourceConfirm: ({ showResourceConfirm }) => {
      return showResourceConfirm
    },
    getShowResource: ({ showResource }) => {
      return showResource
    },
    getShowCategory: ({ showCategory }) => {
      return showCategory
    },
    getShowCofirm: ({ showCofirm }) => {
      return showCofirm
    },
    getIsEdit: ({ isEdit }) => {
      return isEdit
    },
    getResourceAction: ({ resourceAction }) => {
      return resourceAction
    },
    getCategoryAction: ({ categoryAction }) => {
      return categoryAction
    },
    getCategoryActive: ({ categoryActive }) => {
      return categoryActive
    },
    getIsArchiveCategoriesAndResources: ({
      isArchiveCategoriesAndResources
    }) => {
      return isArchiveCategoriesAndResources
    },
    getSelectedResourceIds: ({ selectedResourceIds }) => {
      return selectedResourceIds
    },
    getResources: ({ activeResources, archivedResources }) => {
      return {
        active:
          _.keyBy(
            activeResources?.map((r, i) => {
              r.index = i
              return r
            }),
            'id'
          ) || null,
        archived:
          _.keyBy(
            archivedResources?.map((r, i) => {
              r.index = i
              return r
            }),
            'id'
          ) || null
      }
    },
    getAllCategories: ({ allCategories }) => {
      return allCategories
    },
    getActiveResourcesById: ({ activeResources, activeCategories }) => {
      return (
        ids = [],
        categoriesCallback = () => '_uncategorized_',
        hasFiles = true
      ) => {
        if (
          activeResources == null ||
          activeCategories == null ||
          !_.isArray(ids)
        )
          return null
        const categories = _.transform(
          activeCategories || [],
          (result, item, index) => (result[item.id] = { ...item, index }),
          {}
        )
        const resources = activeResources
          .filter((r) => {
            if (!ids.includes(r.id)) return false
            else if (
              hasFiles &&
              (!_.has(r, 'resourceFiles') ||
                !_.isArray(r.resourceFiles) ||
                r.resourceFiles?.length == 0)
            )
              return false
            return true
          })
          .map((r) => {
            r.path = categoriesCallback(
              r?.categoryId ?? null,
              categories,
              'path'
            )
            r.breadcrumbs = categoriesCallback(
              r?.categoryId ?? null,
              categories,
              'breadcrumbs'
            )
            return r
          })
        return resources.length == 0 ? null : resources
      }
    },
    getAllResourcesExceptById: ({ activeResources, activeCategories }) => {
      return (ids = [], categoriesCallback = () => []) => {
        if (activeResources == null || activeCategories == null) return null
        const transform = (categories, resources) => {
          const categoryMap = _.keyBy(categories, 'id')
          const resourceMap = _.groupBy(resources, 'categoryId')

          const getTree = (categoryId) => {
            const category = categoryMap[categoryId]
            if (!category) return null
            const children = []
            if (resourceMap[categoryId]) {
              children.push(..._.sortBy(resourceMap[categoryId], 'title'))
            }
            const subCategories = categories.filter(
              (c) => c.categoryId == categoryId
            )
            if (subCategories.length > 0) {
              children.push(
                ..._.sortBy(subCategories, 'name').map((c) => ({
                  ...c,
                  children: getTree(c.id)
                }))
              )
            }
            return children
          }
          const rootCategories = categories.filter((c) => !c.categoryId)
          const tree = []
          if (resourceMap[null]) {
            tree.push(..._.sortBy(resourceMap[null], 'title'))
          }
          tree.push(
            ..._.sortBy(rootCategories, 'name').map((c) => ({
              ...c,
              children: getTree(c.id)
            }))
          )
          return tree
        }
        let resources =
          ids == null || !_.isArray(ids) || _.isEmpty(ids)
            ? activeResources
            : activeResources.filter(({ id }) => !ids.includes(id))
        resources = resources.map((r) => ({ ...r, type: 'resource' }))
        let categories = []
        for (const r of resources)
          categories = [
            ...categories,
            ...categoriesCallback(
              r?.categoryId ?? null,
              _.transform(
                activeCategories || [],
                (result, item, index) => (result[item.id] = { ...item, index }),
                {}
              ),
              'categories'
            )
          ]
        categories = _.uniqBy(categories, 'id').map((c) => ({
          ...c,
          type: 'category'
        }))
        return transform(categories, resources)
      }
    },
    hasUncategorizedResources: ({ activeResources }) => {
      return activeResources == null || activeResources.length == 0
        ? 0
        : Number(activeResources.filter((r) => r.categoryId == null).length > 0)
    },
    hasArchivedResources: ({ archivedResources }) => {
      return Number((archivedResources?.length || 0) > 0)
    },
    getFlattenedCategories({
      categoriesDivider,
      allCategories,
      queryCategory
    }) {
      if (allCategories == null) return null

      const childrenMap = {}
      const buildChildrenMap = (categories) => {
        categories.forEach((c) => {
          if (c.categoryId) {
            if (!childrenMap[c.categoryId]) {
              childrenMap[c.categoryId] = []
            }
            childrenMap[c.categoryId].push(c.id)
          }
          if (c.children) {
            buildChildrenMap(c.children)
          }
        })
      }
      buildChildrenMap(allCategories.slice(categoriesDivider.at(-1)))

      const getDescendantIds = (categoryId, ids = []) => {
        const children = childrenMap[categoryId] || []
        for (const childId of children) {
          if (!ids.includes(childId)) {
            ids.push(childId)
            getDescendantIds(childId, ids)
          }
        }
        return ids
      }

      const flatten = (
        categories,
        depth = 0,
        breadcrumbs = [],
        parents = [],
        paths = []
      ) => {
        return categories.flatMap((c) => {
          const _breadcrumbs = [...breadcrumbs, c.name]
          const parentId = c.categoryId ?? null

          const _parents = parentId != null ? [...parents, parentId] : parents
          const _paths = [...paths, c.id]

          const pathString = _paths.join('/')

          const descendantIds = getDescendantIds(c.id)
          const subtree = [c.id, ...descendantIds]

          const flatItem = {
            ...c,
            open: c?.children != null,
            depth,
            breadcrumbs: _breadcrumbs,
            parents: _parents,
            path: pathString,
            subtree
          }

          const children = c.children
            ? flatten(c.children, depth + 1, _breadcrumbs, _parents, _paths)
            : []

          delete flatItem.children

          return [flatItem, ...children]
        })
      }

      let categories = flatten(allCategories)

      if (
        queryCategory != null &&
        queryCategory.replace(/^\s+|\s+$/g, '') != ''
      ) {
        const query = queryCategory.replace(/^\s+|\s+$/g, '').toLowerCase()

        const queried = categories.filter(
          (c) =>
            c.name.toLowerCase().includes(query) ||
            c.path.toLowerCase().includes(query) ||
            c.subtree.some((id) => String(id).toLowerCase().includes(query))
        )

        if (!queried?.length) return []
        else {
          const ids = queried.reduce(
            (a, q) => {
              a.all = [...a.all, ...q.parents, q.id]
              a.queried = [...a.queried, q.id]
              return a
            },
            { all: [], queried: [] }
          )

          categories = categories
            .filter((c) => ids.all.includes(c.id))
            .map((c) => {
              c.highlighted = ids.queried.includes(c.id)
              return c
            })
        }
      }

      return categories
    },
    getOriginalCategoriesDivider({ categoriesDivider }) {
      return categoriesDivider
    },
    getCategoriesDivider({ categoriesDivider, activeResources }) {
      const _notHasPrivateResources = Number(
        !hasPrivateResources(activeResources)
      )
      return categoriesDivider.map((d) => d - _notHasPrivateResources)
    },
    getGlobalUploadProgress({ globalUploadProgress }) {
      return globalUploadProgress
    },
    getDownloadJobId({ downloadJobId }) {
      return downloadJobId
    },
    getSimulationResources({ simulationResources }) {
      return simulationResources
    },
    getUpdateSimulationResources({ updateSimulationResources }) {
      return updateSimulationResources
    }
  },

  actions: {
    async fetchActiveCategories(withLoader = true) {
      if (this.errorActiveCategories != null) this.errorActiveCategories = null
      this.statusActiveCategories = null
      if (withLoader) this.isLoadingActiveCategories = true
      try {
        const result = await resourceCategoriesApi.get('', {
          params: { active: 1 }
        })
        const { error, resourceCategories } = result.data.response
        this.statusActiveCategories = result.data.status
        if (error) this.errorActiveCategories = error.message
        else this.activeCategories = resourceCategories
      } catch (e) {
        this.errorActiveCategories =
          e?.response?.data?.response?.error?.message ||
          e.message ||
          String(e) ||
          JSON.stringify(e)
      }
      this.isLoadingActiveCategories = false
    },
    async fetchActiveResources(withLoader = true) {
      if (this.errorActiveResources != null) this.errorActiveResources = null
      this.statusActiveResources = null
      if (withLoader) this.isLoadingActiveResources = true
      try {
        const result = await resourceDataApi.get('', { params: { active: 1 } })
        const { error, resourceData } = result.data.response
        this.statusActiveResources = result.data.status
        if (error) this.errorActiveResources = error.message
        else {
          this.activeResources = resourceData
          if (this.activeResources?.length > 0)
            for (const resource of this.activeResources.entries())
              if (
                _.has(resource, 'resourceFiles') &&
                !_.isEmpty(resource.resourceFiles) &&
                _.isArray(resource.resourceFiles)
              )
                for (const file of resource.resourceFiles)
                  if (_.has(this.fileArchives, file.id)) {
                    file.url = this.fileArchives[file.id].url
                    file.expireTs = this.fileArchives[file.id].expireTs
                    file.type = this.fileArchives[file.id].type
                    file.size = this.fileArchives[file.id].size
                  }
        }
      } catch (e) {
        this.errorActiveResources =
          e?.response?.data?.response?.error?.message ||
          e.message ||
          String(e) ||
          JSON.stringify(e)
      }
      this.isLoadingActiveResources = false
    },
    async fetchArchivedResources(withLoader = true) {
      if (this.errorArchivedResources != null)
        this.errorArchivedResources = null
      this.statusArchivedResources = null
      if (withLoader) this.isLoadingArchivedResources = true
      try {
        const result = await resourceDataApi.get('', { params: { active: 0 } })
        const { error, resourceData } = result.data.response
        this.statusArchivedResources = result.data.status
        if (error) this.errorArchivedResources = error.message
        else {
          this.archivedResources = resourceData
          if (this.archivedResources?.length > 0)
            for (const resource of this.archivedResources.entries())
              if (
                _.has(resource, 'resourceFiles') &&
                !_.isEmpty(resource.resourceFiles) &&
                _.isArray(resource.resourceFiles)
              )
                for (const file of resource.resourceFiles)
                  if (_.has(this.fileArchives, file.id)) {
                    file.url = this.fileArchives[file.id].url
                    file.expireTs = this.fileArchives[file.id].expireTs
                    file.type = this.fileArchives[file.id].type
                    file.size = this.fileArchives[file.id].size
                  }
        }
      } catch (e) {
        this.errorArchivedResources =
          e?.response?.data?.response?.error?.message ||
          e.message ||
          String(e) ||
          JSON.stringify(e)
      }
      this.isLoadingArchivedResources = false
    },
    async fetchFilesArchive() {
      const _indexedDb = await filesIndexedDb.all()
      _.forEachRight(_indexedDb, (h, i) => {
        if (new Date(h.expireTs) < new Date(new Date().getTime() + 15000)) {
          filesIndexedDb.remove(h.id)
          _.pullAt(_indexedDb, i)
        }
      })
      this.fileArchives = _.keyBy(_indexedDb, 'id')
    },
    async getFileUrl(file, disposition = 'inline') {
      if (
        disposition === 'inline' &&
        _.has(this.fileArchives, file.id) &&
        !this.fileLoaders?.[file.id]?.loading &&
        new Date(this.fileArchives[file.id].expireTs) >=
          new Date(new Date().getTime() + 10000)
      ) {
        const {
          url,
          expireTs,
          type: fileType,
          size
        } = this.fileArchives[file.id]
        file.url = url
        file.expireTs = expireTs
        file.type = fileType
        file.size = size
      } else if (!this.fileLoaders?.[file.id]?.loading) {
        this.fileLoaders[file.id] = {
          error: null,
          status: null,
          loading: true
        }
        try {
          const result = await blobApi.get('', {
            params: {
              id: file.id,
              container: 'resources',
              name:
                `${file?.path || '_uncategorized_'}/` +
                (file.resourceId == null ? '' : `${file.resourceId}/`) +
                file.fileName,
              duration: 1,
              disposition
            }
          })
          const { error, blob } = result.data.response
          this.fileLoaders[file.id].status = result.data.status
          if (error) this.fileLoaders[file.id].error = error.message
          else {
            const { url, expireTs, type: fileType, size } = blob
            if (
              url == null ||
              expireTs == null ||
              fileType == null ||
              size == null
            ) {
              file.url = null
              file.expireTs = null
              file.type = null
              file.size = null
              delete this.fileArchives[file.id]
              await filesIndexedDb.remove(file.id)
            } else {
              file.url = url
              file.expireTs = expireTs
              file.type = fileType
              file.size = size
              if (disposition === 'inline') {
                this.fileArchives[file.id] = {
                  url,
                  expireTs,
                  type: fileType,
                  size
                }
                await filesIndexedDb.set(file.id, {
                  id: file.id,
                  url,
                  expireTs,
                  type: fileType,
                  size
                })
              }
              this.fileLoaders[file.id].loading = false
            }
          }
        } catch (e) {
          this.fileLoaders[file.id].error =
            e?.response?.data?.response?.error?.message ||
            e.message ||
            String(e) ||
            JSON.stringify(e)
          this.fileLoaders[file.id].loading = false
        }
      }
    },
    async archivingResources(resources) {
      this.isActionMultiple.archivingResources = resources?.length > 1
      this.errorArchivingResources = null
      this.isLoadingArchivingResources = true
      this.statusArchivingResources = null
      const payload = {
        where: {
          id: {
            in: _.map(resources, (i) => i.id)
          }
        },
        data: {
          deleteTs: 'softDelete'
        },
        select: {
          id: true,
          updateTs: true,
          deleteTs: true
        }
      }
      try {
        const result = await resourceDataApi.patch('', payload)
        const { error, resourceData } = result.data.response
        this.statusArchivingResources = result.data.status
        if (error) this.errorArchivingResources = error.message
        else if (resourceData?.length) {
          _.forEach(resourceData, (r) => {
            const resource =
              _.remove(this.activeResources, { id: r.id })[0] || null
            if (resource != null) {
              resource.updateTs = r.updateTs
              resource.deleteTs = r.deleteTs
              this.archivedResources = [
                ..._(this.archivedResources || []),
                resource
              ]
            }
          })
        }
      } catch (e) {
        this.errorArchivingResources =
          e?.response?.data?.response?.error?.message ||
          e.message ||
          String(e) ||
          JSON.stringify(e)
      }
      this.isLoadingArchivingResources = false
      delete this.isActionMultiple.archivingResources
    },
    async restoringResources(resources) {
      this.isActionMultiple.restoringResources = resources?.length > 1
      this.errorRestoringResources = null
      this.isLoadingRestoringResources = true
      this.statusRestoringResources = null
      const payload = {
        where: {
          id: {
            in: _.map(resources, (i) => i.id)
          }
        },
        data: {
          deleteTs: 'restore'
        },
        select: {
          id: true,
          updateTs: true,
          deleteTs: true
        }
      }
      try {
        const result = await resourceDataApi.patch('', payload)
        const { error, resourceData } = result.data.response
        this.statusRestoringResources = result.data.status
        if (error) this.errorRestoringResources = error.message
        else if (resourceData?.length) {
          _.forEach(resourceData, (r) => {
            const resource =
              _.remove(this.archivedResources, { id: r.id })[0] || null
            if (resource != null) {
              resource.updateTs = r.updateTs
              resource.deleteTs = r.deleteTs
              this.activeResources = [
                ..._(this.activeResources || []),
                resource
              ]
            }
          })
        }
      } catch (e) {
        this.errorRestoringResources =
          e?.response?.data?.response?.error?.message ||
          e.message ||
          String(e) ||
          JSON.stringify(e)
      }
      this.isLoadingRestoringResources = false
      delete this.isActionMultiple.restoringResources
    },
    async purgingResources(resources) {
      this.isActionMultiple.purgingResources = resources?.length > 1
      this.errorPurgingResources = null
      this.isLoadingPurgingResources = true
      this.statusPurgingResources = null
      const payload = {
        where: {
          id: {
            in: _.map(resources, (i) => i.id)
          }
        },
        data: {
          purgeTs: 'useUtcDate'
        },
        select: {
          id: true,
          updateTs: true,
          purgeTs: true
        }
      }
      try {
        const result = await resourceDataApi.patch('', payload)
        const { error, resourceData } = result.data.response
        this.statusPurgingResources = result.data.status
        if (error) this.errorPurgingResources = error.message
        else if (resourceData?.length) {
          const payload = {
            where: {
              resourceId: {
                in: _.map(resources, (i) => i.id)
              },
              deleteTs: { isNull: true }
            },
            data: {
              deleteTs: 'softDelete'
            },
            select: {
              id: true,
              resourceId: true,
              categoryId: true,
              fileName: true,
              updateTs: true,
              deleteTs: true
            }
          }
          const result = await resourceFilesApi.patch('', payload)
          const { error, resourceFiles } = result.data.response
          this.statusPurgingResources = result.data.status
          if (error) this.errorPurgingResources = error.message
          else if (resourceFiles?.length > 0) {
            const pathsToPurge = _.uniq(
              _.map(resourceFiles, ({ categoryId, resourceId }) => {
                const _path = this.getRelativeCategories(
                  categoryId,
                  this.getResourceData.categories,
                  'path'
                )
                return `${_path}/${resourceId}/`
              })
            )
            const payload = {
              container: 'resources',
              blob: pathsToPurge,
              deleteParent: true
            }
            const result = await blobApi.delete('', { data: payload })
            const { error } = result.data.response
            this.statusPurgingResources = result.data.status
            if (error) this.errorPurgingResources = error.message
            else
              for (const f of resourceFiles) {
                delete this.fileArchives[f.id]
                await filesIndexedDb.remove(f.id)
                delete this.fileLoaders[f.id]
              }
          }
          if (this.errorPurgingResources == null)
            _.forEach(resourceData, (r) =>
              _.remove(this.archivedResources, ({ id }) => id == r.id)
            )
        }
      } catch (e) {
        this.errorPurgingResources =
          e?.response?.data?.response?.error?.message ||
          e.message ||
          String(e) ||
          JSON.stringify(e)
      }
      this.isLoadingPurgingResources = false
      delete this.isActionMultiple.purgingResources
    },
    async movingResources(resources) {
      this.isActionMultiple.movingResources = resources?.length > 1
      this.errorMovingResources = null
      this.isLoadingMovingResources = true
      this.statusMovingResources = null
      const payload = {
        where: {
          id: {
            in: _.map(resources, (i) => i.id)
          }
        },
        data: {
          categoryId: this.editCategory?.id || null
        },
        select: {
          id: true,
          categoryId: true,
          updateTs: true
        }
      }
      try {
        const result = await resourceDataApi.patch('', payload)
        const { error, resourceData } = result.data.response
        this.statusMovingResources = result.data.status
        if (error) this.errorMovingResources = error.message
        else if (resourceData?.length) {
          const payload = {
            where: {
              resourceId: {
                in: _.map(resources, (i) => i.id)
              }
            },
            data: {
              categoryId: this.editCategory?.id || null
            },
            select: {
              id: true,
              fileName: true,
              categoryId: true,
              updateTs: true
            }
          }
          const result = await resourceFilesApi.patch('', payload)
          const { error, resourceFiles } = result.data.response
          this.statusMovingResources = result.data.status
          if (error) this.errorMovingResources = error.message
          else if (resourceFiles?.length) {
            const source = _.uniq(
              _.map(resources, ({ categoryId, id }) => {
                const _path = this.getRelativeCategories(
                  categoryId,
                  this.getResourceData.categories,
                  'path'
                )
                return `${_path}/${id}/`
              })
            )
            const destination = `${this.editCategory?.path || '_uncategorized_'}/`
            const payload = {
              container: 'resources',
              source,
              destination,
              duration: 24,
              unit: 'hour',
              keepId: true
            }
            const result = await blobApi.patch('', payload)
            let { error, blob } = result.data.response
            this.statusMovingResources = result.data.status
            if (error) this.errorMovingResources = error.message
            else {
              for (const b of blob) {
                for (const [i, f] of resourceFiles.entries())
                  if (f.fileName == b.fileName) {
                    const _b = {
                      url: b.url,
                      expireTs: b.expireTs,
                      type: b.type,
                      size: b.size
                    }
                    _.assign(resourceFiles[i], _b)
                    this.fileArchives[f.id] = _b
                    await filesIndexedDb.set(f.id, {
                      id: f.id,
                      ..._b
                    })
                    break
                  }
              }
            }
            if (this.errorMovingResources == null)
              _.forEach(resourceFiles, (f) => {
                const index = _.findIndex(resourceData, { id: f.resourceId })
                if (index != -1)
                  resourceData[index].resourceFiles = [
                    ...(resourceData[index]?.resourceFiles || []),
                    f
                  ]
              })
          }
          if (this.errorMovingResources == null) {
            _.forEach(resourceData, (r) => {
              const index = _.findIndex(this.activeResources, { id: r.id })
              if (index != -1) {
                this.activeResources[index].categoryId = this.editCategory?.id
                this.activeResources[index].resourceCategory = this.editCategory
                this.activeResources[index].updateTs = r.updateTs
                if (
                  _.has(r, 'resourceFiles') &&
                  _.has(this.activeResources[index], 'resourceFiles')
                )
                  _.forEach(r.resourceFiles, (f) => {
                    const findex = _.findIndex(
                      this.activeResources[index].resourceFiles,
                      { id: f.id }
                    )
                    if (findex != -1) {
                      this.activeResources[index].resourceFiles[
                        findex
                      ].categoryId = this.editCategory?.id
                      for (const k of [
                        'updateTs',
                        'url',
                        'expireTs',
                        'type',
                        'size'
                      ])
                        this.activeResources[index].resourceFiles[findex][k] =
                          f[k]
                    }
                  })
              }
            })
            if (this.viewResource != null) {
              const breadcrumbs = this.getRelativeCategories(
                this.editCategory?.id || null,
                this.getResourceData.categories,
                'breadcrumbs'
              )
              this.viewResource.breadcrumbs =
                breadcrumbs.length > 0 ? breadcrumbs : null
              this.viewResource.path = this.getRelativeCategories(
                this.editCategory?.id || null,
                this.getResourceData.categories,
                'path'
              )
            }
            this.editCategory = null
          }
        }
      } catch (e) {
        this.errorMovingResources =
          e?.response?.data?.response?.error?.message ||
          e.message ||
          String(e) ||
          JSON.stringify(e)
      }
      this.isLoadingMovingResources = false
      delete this.isActionMultiple.movingResources
    },
    async assignResourcesToSimulation() {
      if (!_.isEmpty(this.simulationResources)) {
        const resourceIds = _.difference(
          this.selectedResourceIds,
          this.simulationResources?.resources ?? []
        )
        if (resourceIds.length > 0 && this.simulationResources.id != null) {
          this.isActionMultiple.assignResourcesToSimulation =
            this.selectedResourceIds.length > 1
          this.errorAssignResourcesToSimulation = null
          this.isLoadingAssignResourcesToSimulation = true
          this.statusAssignResourcesToSimulation = null
          const payload = {
            data: resourceIds.map((resourceId) => ({
              simulationId: this.simulationResources.id,
              resourceId
            }))
          }
          try {
            const result = await simulationResourcesApi.post('', payload)
            let { error, simulationResources } = result.data.response
            this.statusAssignResourcesToSimulation = result.data.status
            if (error) this.errorAssignResourcesToSimulation = error.message
            else
              this.updateSimulationResources = {
                id: this.simulationResources.id,
                resources: simulationResources,
                updateType: 'assign'
              }
          } catch (e) {
            this.errorAssignResourcesToSimulation =
              e?.response?.data?.response?.error?.message ||
              e.message ||
              String(e) ||
              JSON.stringify(e)
          }
          this.isLoadingAssignResourcesToSimulation = false
          delete this.isActionMultiple.assignResourcesToSimulation
        }
      }
    },
    async removeResourcesFromSimulation() {
      if (!_.isEmpty(this.simulationResources)) {
        const resourceIds = _.intersection(
          this.selectedResourceIds,
          this.simulationResources.resources
        )
        if (resourceIds.length > 0 && this.simulationResources.id != null) {
          this.isActionMultiple.removeResourcesFromSimulation =
            this.selectedResourceIds.length > 1
          this.errorRemoveResourcesFromSimulation = null
          this.isLoadingRemoveResourcesFromSimulation = true
          this.statusRemoveResourcesFromSimulation = null
          const payload = {
            where: {
              resourceId: {
                in: resourceIds
              },
              simulationId: this.simulationResources.id,
              deleteTs: { isNull: true }
            },
            data: {
              deleteTs: 'softDelete'
            }
          }
          try {
            const result = await simulationResourcesApi.patch('', payload)
            let { error, simulationResources } = result.data.response
            this.statusRemoveResourcesFromSimulation = result.data.status
            if (error) this.errorRemoveResourcesFromSimulation = error.message
            else
              this.updateSimulationResources = {
                id: this.simulationResources.id,
                resources: simulationResources,
                updateType: 'remove'
              }
          } catch (e) {
            this.errorRemoveResourcesFromSimulation =
              e?.response?.data?.response?.error?.message ||
              e.message ||
              String(e) ||
              JSON.stringify(e)
          }
          this.isLoadingRemoveResourcesFromSimulation = false
          delete this.isActionMultiple.removeResourcesFromSimulation
        }
      }
    },
    async handleResourceAction() {
      switch (this.resourceAction) {
        case 'archive':
          await this.archivingResources(
            this.selectedResourceIds.map((id) => ({ id }))
          )
          this.selectedResourceIds = []
          break
        case 'restore':
          await this.restoringResources(
            this.selectedResourceIds.map((id) => ({ id }))
          )
          this.selectedResourceIds = []
          break
        case 'purge':
          await this.purgingResources(
            this.selectedResourceIds.map((id) => ({ id }))
          )
          this.selectedResourceIds = []
          if (this.showResource) {
            this.showResource = false
            this.viewResource = null
          }
          break
        case 'move':
          await this.movingResources(
            this.getResourceData.data.filter(({ id }) =>
              this.selectedResourceIds.includes(id)
            )
          )
          this.showCategory = false
          this.selectedResourceIds = []
          break
        case 'assignSimulation':
          await this.assignResourcesToSimulation()
          this.selectedResourceIds = []
          break
        case 'assignSimulationAndClose':
          await this.assignResourcesToSimulation()
          this.selectedResourceIds = []
          break
        case 'removeSimulation':
          await this.removeResourcesFromSimulation()
          this.selectedResourceIds = []
          break
        case 'removeSimulationAndClose':
          await this.removeResourcesFromSimulation()
          this.selectedResourceIds = []
          break
        default:
          break
      }
      if (this.showConfirm) this.showConfirm = false
      if (this.resourceAction != null) this.resourceAction = null
    },
    async creatingResource(resource, assignSimulation = false) {
      let { title, description, category, institution, files } = resource
      if (title != null) title = title.replace(/^\s+|\s+$/g, '')
      if (description != null)
        description = description.replace(/^\s+|\s+$/g, '')
      this.errorCreatingResource = null
      this.isLoadingCreatingResource = true
      this.statusCreatingResource = null
      const payload = {
        data: {
          title,
          description,
          categoryId: category?.id || null,
          institutionId: institution?.id || null
        },
        select: {
          id: true,
          title: true,
          description: true,
          categoryId: true,
          institutionId: true,
          userData: {
            id: true,
            provider: true,
            countryCode: true,
            createTs: true,
            updateTs: true,
            deleteTs: true
          }
        }
      }
      try {
        const result = await resourceDataApi.post('', payload)
        const { error, resourceData } = result.data.response
        this.statusCreatingResource = result.data.status
        if (error) this.errorCreatingResource = error.message
        else {
          if (
            _.has(resourceData, 'institutionId') &&
            resourceData.institutionId == institution?.id
          )
            resourceData.institution = institution
          if (
            _.has(resourceData, 'categoryId') &&
            resourceData.categoryId == category?.id
          )
            resourceData.resourceCategory = _.pick(category, [
              'id',
              'userId',
              'institutionId',
              'categoryId',
              'name',
              'container',
              'createTs',
              'updateTs',
              'deleteTs'
            ])
          if (files) {
            const path = files[0].path || '_uncategorized_'
            const payload = {
              container: 'resources',
              path,
              id: resourceData.id,
              duration: 5,
              unit: 'minute',
              files: _.reduce(
                files,
                (a, { name }, i) => {
                  a[i] = name
                  return a
                },
                {}
              )
            }
            const result = await blobApi.put('', payload)
            const { error, blob } = result.data.response
            this.statusCreatingResource = result.data.status
            if (error) this.errorCreatingResource = error.message
            else {
              const totalSize = files.reduce((sum, f) => sum + f.size, 0)
              this.globalUploadProgress = 0
              let totalLoaded = 0
              const uploadFiles = await Promise.all(
                files.map(async (file, index) => {
                  const f = blob[index]
                  if (file && _.has(f, 'url') && !_.isEmpty(f.url)) {
                    const blobClient = new BlockBlobClient(f.url)
                    try {
                      await blobClient.uploadData(file, {
                        blobHTTPHeaders: {
                          blobContentType:
                            file.type || 'application/octet-stream'
                        },
                        onProgress: (event) => {
                          totalLoaded += event.loadedBytes
                          this.globalUploadProgress = Math.min(
                            Number(
                              ((totalLoaded / totalSize) * 100).toFixed(2)
                            ),
                            100
                          )
                        }
                      })
                      return { index, status: 'success' }
                    } catch (err) {
                      return { index, status: 'failed', error: err.message }
                    }
                  }
                })
              )
              const errors = uploadFiles.filter((r) => r.status === 'failed')
              this.globalUploadProgress = null
              if (errors.length > 0)
                this.errorCreatingResource.value = `Upload failed for the following file(s): ${errors.map(({ index, error }) => `File '${files[parseInt(index)].name}' (${error})`).join(', ')}`
              else {
                const payload = {
                  container: 'resources',
                  path,
                  id: resourceData.id,
                  duration: 24,
                  unit: 'hour',
                  files: _.reduce(
                    files,
                    (a, { name }, i) => {
                      a[i] = name
                      return a
                    },
                    {}
                  )
                }
                const result = await blobApi.post('', payload)
                const { error, blob } = result.data.response
                this.statusCreatingResource = result.data.status
                if (error) this.errorCreatingResource = error.message
                else {
                  const payload = {
                    data: _.values(blob).map((f) => ({
                      institutionId: institution?.id || null,
                      resourceId: resourceData.id,
                      categoryId: category?.id || null,
                      container: 'resources',
                      fileName: f.fileName
                    }))
                  }
                  const result = await resourceFilesApi.post('', payload)
                  const { error, resourceFiles } = result.data.response
                  this.statusCreatingResource = result.data.status
                  if (error) this.errorCreatingResource = error.message
                  else {
                    const filesByName = _.keyBy(blob, 'fileName')
                    resourceFiles.forEach(async (f) => {
                      const file = filesByName[f.fileName]
                      if (file) {
                        for (const k of ['url', 'expireTs', 'type', 'size'])
                          if (_.has(file, k)) f[k] = file[k]
                        this.fileArchives[f.id] = {
                          url: file.url,
                          expireTs: file.expireTs,
                          type: file.type,
                          size: file.size
                        }
                        await filesIndexedDb.set(f.id, {
                          id: f.id,
                          url: file.url,
                          expireTs: file.expireTs,
                          type: file.type,
                          size: file.size
                        })
                      }
                    })
                    resourceData.resourceFiles = resourceFiles
                  }
                }
              }
            }
          }
          this.activeResources.unshift(resourceData)
          if (assignSimulation) {
            this.selectedResourceIds = [resourceData.id]
            this.resourceAction = 'assignSimulation'
            await this.handleResourceAction()
          }
        }
      } catch (e) {
        this.errorCreatingResource =
          e?.response?.data?.response?.error?.message ||
          e.message ||
          String(e) ||
          JSON.stringify(e)
      }
      this.isLoadingCreatingResource = false
    },
    async editingResource(resource, assignSimulation = false) {
      const { id, index } = resource
      delete resource.id
      delete resource.index
      if (!_.isEmpty(resource)) {
        this.errorEditingResource = null
        this.isLoadingEditingResource = true
        this.statusEditingResource = null
        try {
          if (_.has(resource, 'files') && resource.files?.toArchive != null) {
            const payload = {
              container: 'resources',
              blob: _.map(
                resource.files.toArchive,
                (r) => `${r?.path || '_uncategorized_'}/` + `${id}/` + r.name
              )
            }
            const result = await blobApi.delete('', { data: payload })
            const { error } = result.data.response
            this.statusEditingResource = result.data.status
            if (error) this.errorEditingResource = error.message
            else {
              const removedFileIds = _.map(
                resource.files.toArchive,
                (f) => f.id
              )
              const payload = {
                where: {
                  id: {
                    in: removedFileIds
                  }
                },
                data: {
                  deleteTs: 'softDelete'
                },
                select: {
                  id: true
                }
              }
              const result = await resourceFilesApi.patch('', payload)
              const { error } = result.data.response
              this.statusEditingResource = result.data.status
              if (error) this.errorEditingResource = error.message
              else
                _.remove(this.activeResources[index].resourceFiles, (f) =>
                  removedFileIds.includes(f.id)
                )
              _.forEach(removedFileIds, async (_id) => {
                delete this.fileArchives[_id]
                await filesIndexedDb.remove(_id)
                delete this.fileLoaders[_id]
              })
            }
          }
          if (
            this.errorEditingResource == null &&
            _.has(resource, 'category')
          ) {
            const { new: _new, old: _old } = resource.category
            const destination = `${_new?.path || '_uncategorized_'}/${id}/`
            const destinationPath = _new?.path || '_uncategorized_'

            const payload = {
              container: 'resources',
              source: `${_old?.path || '_uncategorized_'}/${id}/`,
              destination,
              duration: 24,
              unit: 'hour'
            }
            const result = await blobApi.patch('', payload)
            let { error, blob } = result.data.response
            this.statusEditingResource = result.data.status
            if (error) this.errorEditingResource = error.message
            else {
              blob = _.keyBy(blob, 'name')
              if (this.activeResources[index].resourceFiles != null) {
                const fileIds = this.activeResources[index].resourceFiles.map(
                  (f) => f.id
                )
                const payload = {
                  where: {
                    id: {
                      in: fileIds
                    }
                  },
                  data: {
                    categoryId: _new?.id || null
                  },
                  select: {
                    id: true,
                    updateTs: true
                  }
                }
                const result = await resourceFilesApi.patch('', payload)
                let { error, resourceFiles } = result.data.response
                this.statusEditingResource = result.data.status
                if (error) this.errorEditingResource = error.message
                else {
                  resourceFiles = _.keyBy(resourceFiles, 'id')
                  _.forEach(fileIds, async (_id, i) => {
                    this.activeResources[index].resourceFiles[i].updateTs =
                      resourceFiles[_id].updateTs
                    this.activeResources[index].resourceFiles[i].path =
                      destinationPath
                    this.activeResources[index].resourceFiles[i].categoryId =
                      _new?.id || null
                    const {
                      url,
                      expireTs,
                      type: fileType,
                      size
                    } = blob[
                      destination +
                        this.activeResources[index].resourceFiles[i].fileName
                    ]
                    this.activeResources[index].resourceFiles[i].url = url
                    this.activeResources[index].resourceFiles[i].expireTs =
                      expireTs
                    this.activeResources[index].resourceFiles[i].type = fileType
                    this.activeResources[index].resourceFiles[i].size = size
                    this.fileArchives[_id] = {
                      url,
                      expireTs,
                      type: fileType,
                      size
                    }
                    await filesIndexedDb.set(_id, {
                      id: _id,
                      url,
                      expireTs,
                      type: fileType,
                      size
                    })
                  })
                }
              }
            }
            if (this.errorEditingResource == null) {
              resource.categoryId = _new?.id || null
              resource.breadcrumbs = _new?.breadcrumbs || null
              resource.path = destinationPath
              resource._new = _new
              delete resource.category
            }
          }
          if (
            this.errorEditingResource == null &&
            _.has(resource, 'files') &&
            resource.files?.current != null
          ) {
            const path = resource._path || '_uncategorized_'
            const institutionId = resource._institutionId || null
            const categoryId = resource._categoryId || null
            delete resource._path
            delete resource._institutionId
            delete resource._categoryId
            const payload = {
              container: 'resources',
              path,
              id,
              duration: 5,
              unit: 'minute',
              files: _.reduce(
                resource.files.current,
                (a, { name }, i) => {
                  a[i] = name
                  return a
                },
                {}
              )
            }
            const result = await blobApi.put('', payload)
            const { error, blob } = result.data.response
            this.statusEditingResource = result.data.status
            if (error) this.errorEditingResource = error.message
            else {
              const totalSize = resource.files.current.reduce(
                (sum, f) => sum + f.size,
                0
              )
              this.globalUploadProgress = 0
              let totalLoaded = 0
              const uploadFiles = await Promise.all(
                resource.files.current.map(async (file, index) => {
                  const f = blob[index]
                  if (file && _.has(f, 'url') && !_.isEmpty(f.url)) {
                    const blobClient = new BlockBlobClient(f.url)
                    try {
                      await blobClient.uploadData(file, {
                        blobHTTPHeaders: {
                          blobContentType:
                            file.type || 'application/octet-stream'
                        },
                        onProgress: (event) => {
                          totalLoaded += event.loadedBytes
                          this.globalUploadProgress = Math.min(
                            Number(
                              ((totalLoaded / totalSize) * 100).toFixed(2)
                            ),
                            100
                          )
                        }
                      })
                      return { index, status: 'success' }
                    } catch (err) {
                      return { index, status: 'failed', error: err.message }
                    }
                  }
                })
              )
              this.globalUploadProgress = null
              const errors = uploadFiles.filter((r) => r.status === 'failed')
              if (errors.length > 0)
                this.errorEditingResource.value = `Upload failed for the following file(s): ${errors.map(({ index, error }) => `File '${resource.files.current[parseInt(index)].name}' (${error})`).join(', ')}`
              else {
                const payload = {
                  container: 'resources',
                  path,
                  id,
                  duration: 24,
                  unit: 'hour',
                  files: _.reduce(
                    resource.files.current,
                    (a, { name }, i) => {
                      a[i] = name
                      return a
                    },
                    {}
                  )
                }
                const result = await blobApi.post('', payload)
                const { error, blob } = result.data.response
                this.statusEditingResource = result.data.status
                if (error) this.errorEditingResource = error.message
                else {
                  const payload = {
                    data: _.values(blob).map((f) => ({
                      institutionId,
                      resourceId: id,
                      categoryId,
                      container: 'resources',
                      fileName: f.fileName
                    }))
                  }
                  const result = await resourceFilesApi.post('', payload)
                  const { error, resourceFiles } = result.data.response
                  this.statusEditingResource = result.data.status
                  if (error) this.errorEditingResource = error.message
                  else {
                    const filesByName = _.keyBy(blob, 'fileName')
                    resourceFiles.forEach(async (f) => {
                      const file = filesByName[f.fileName]
                      if (file) {
                        for (const k of ['url', 'expireTs', 'type', 'size'])
                          if (_.has(file, k)) f[k] = file[k]
                        this.fileArchives[f.id] = {
                          url: file.url,
                          expireTs: file.expireTs,
                          type: file.type,
                          size: file.size
                        }
                        await filesIndexedDb.set(f.id, {
                          id: f.id,
                          url: file.url,
                          expireTs: file.expireTs,
                          type: file.type,
                          size: file.size
                        })
                      }
                    })
                    this.activeResources[index].resourceFiles.push(
                      ...resourceFiles
                    )
                  }
                }
              }
            }
          }
          if (this.errorEditingResource == null) {
            if (_.has(resource, 'files')) delete resource.files
            const {
              institution,
              _new,
              // eslint-disable-next-line no-unused-vars
              breadcrumbs,
              // eslint-disable-next-line no-unused-vars
              path,
              ...payload
            } = resource
            if (_.isObjectLike(payload) && !_.isEmpty(payload)) {
              const result = await resourceDataApi.patch('', {
                where: { id },
                data: payload
              })
              const { error } = result.data.response
              this.statusEditingResource = result.data.status
              if (error) this.errorEditingResource = error.message
              else {
                if (_.has(payload, 'categoryId')) {
                  if (_new == null) {
                    this.activeResources[index].resourceCategory = null
                  } else {
                    this.activeResources[index].resourceCategory = _.pick(
                      _new,
                      [
                        'id',
                        'userId',
                        'institutionId',
                        'categoryId',
                        'name',
                        'container',
                        'createTs',
                        'updateTs',
                        'deleteTs'
                      ]
                    )
                  }
                }
                if (_.has(payload, 'institutionId')) {
                  this.activeResources[index].institution = institution
                  if (
                    _.has(this.activeResources[index], 'resourceFiles') &&
                    this.activeResources[index].resourceFiles != null &&
                    !_.isEmpty(this.activeResources[index].resourceFiles)
                  ) {
                    const payloadInstitution = {
                      where: { resourceId: id },
                      data: { institutionId: payload.institutionId || null }
                    }
                    const result = await resourceFilesApi.patch(
                      '',
                      payloadInstitution
                    )
                    const { error } = result.data.response
                    this.statusEditingResource = result.data.status
                    if (error) this.errorEditingResource = error.message
                    else {
                      _.forEach(
                        this.activeResources[index].resourceFiles,
                        (f) => (f.institutionId = payload.institutionId)
                      )
                    }
                  }
                }
                for (const k of _.keys(payload))
                  this.activeResources[index][k] = payload[k]
              }
            }
          }
          if (this.errorEditingResource == null && this.viewResource != null) {
            if (this.viewResource != null) {
              const { breadcrumbs, path } = resource
              const viewResource = this.activeResources[index]
              viewResource.breadcrumbs =
                breadcrumbs != null && breadcrumbs.length > 0
                  ? breadcrumbs
                  : null
              viewResource.path = path || '_uncategorized_'
              this.vewResource = viewResource
            }
            if (assignSimulation) {
              this.selectedResourceIds = [this.activeResources[index].id]
              this.resourceAction = 'assignSimulation'
              await this.handleResourceAction()
            }
          }
        } catch (e) {
          this.errorEditingResource =
            e?.response?.data?.response?.error?.message ||
            e.message ||
            String(e) ||
            JSON.stringify(e)
        }
        this.isLoadingEditingResource = false
      }
    },
    async creatingCategory() {
      this.editCategory.name = this.editCategory.name.replace(/^\s+|\s+$/g, '')
      const { institution, userId, user, ...data } = this.editCategory
      this.errorCreatingCategory = null
      this.isLoadingCreatingCategory = true
      this.statusCreatingCategory = null
      const payload = {
        data,
        select: {
          id: true,
          createTs: true,
          updateTs: true,
          deleteTs: true
        }
      }
      try {
        const result = await resourceCategoriesApi.post('', payload)
        let { error, resourceCategories } = result.data.response
        this.statusCreatingCategory = result.data.status
        if (error) this.errorCreatingCategory = error.message
        else {
          resourceCategories = {
            ...data,
            ...resourceCategories,
            institution,
            userId,
            user
          }
          this.activeCategories.unshift(resourceCategories)
          this.editCategory = null
        }
      } catch (e) {
        this.errorCreatingCategory =
          e?.response?.data?.response?.error?.message ||
          e.message ||
          String(e) ||
          JSON.stringify(e)
      }
      this.isLoadingCreatingCategory = false
    },
    async editingCategory() {
      this.editCategory.name = this.editCategory.name.replace(/^\s+|\s+$/g, '')
      this.errorEditingCategory = null
      this.isLoadingEditingCategory = true
      this.statusEditingCategory = null
      const payload = {
        where: { id: this.editCategory.id },
        data: {
          name: this.editCategory.name
        }
      }
      try {
        const result = await resourceCategoriesApi.patch('', payload)
        const { error } = result.data.response
        this.statusEditingCategory = result.data.status
        if (error) this.errorEditingCategory = error.message
        else {
          this.activeCategories[
            this.getResourceData.categories[this.editCategory.id].index
          ].name = this.editCategory.name
          this.editCategory = null
        }
      } catch (e) {
        this.errorEditingResource =
          e?.response?.data?.response?.error?.message ||
          e.message ||
          String(e) ||
          JSON.stringify(e)
      }
      this.isLoadingEditingCategory = false
    },
    async archivingCategories() {
      const ids = _.map(this.editCategory.categories, 'id')
      this.isActionMultiple.archivingCategories = ids.length > 1
      this.errorArchivingCategories = null
      this.isLoadingArchivingCategories = true
      this.statusArchivingCategories = null
      const payload = {
        where: {
          id: {
            in: ids
          }
        },
        data: {
          deleteTs: 'softDelete'
        },
        select: {
          id: true,
          updateTs: true,
          deleteTs: true
        }
      }
      try {
        const result = await resourceCategoriesApi.patch('', payload)
        const { error, resourceCategories } = result.data.response
        this.statusArchivingCategories = result.data.status
        if (error) this.errorArchivingCategories = error.message
        else if (resourceCategories?.length) {
          const payload = {
            where: {
              categoryId: {
                in: ids
              }
            },
            data: {
              categoryId: null
            },
            select: {
              id: true,
              updateTs: true
            }
          }
          const result = await resourceDataApi.patch('', payload)
          const { error, resourceData } = result.data.response
          this.statusArchivingCategories = result.data.status
          if (error) this.errorArchivingCategories = error.message
          else if (resourceData?.length) {
            const payload = {
              where: {
                resourceId: {
                  in: resourceData.map(({ id }) => id)
                },
                categoryId: {
                  in: ids
                }
              },
              data: {
                categoryId: null
              },
              select: {
                id: true,
                resourceId: true,
                categoryId: true,
                fileName: true,
                updateTs: true
              }
            }
            const result = await resourceFilesApi.patch('', payload)
            const { error, resourceFiles } = result.data.response
            this.statusArchivingCategories = result.data.status
            if (error) this.errorArchivingCategories = error.message
            else if (resourceFiles?.length) {
              const source = _.uniq(
                _.map(resourceFiles, ({ resourceId }) => {
                  const _fpath = this.getRelativeCategories(
                    this.getResources.active?.[resourceId]?.categoryId ||
                      this.getResources.archived?.[resourceId]?.categoryId,
                    this.getResourceData.categories,
                    'path'
                  )
                  return `${_fpath}/${resourceId}/`
                })
              )
              const destination = '_uncategorized_/'
              const payload = {
                container: 'resources',
                source,
                destination,
                duration: 24,
                unit: 'hour',
                keepId: true
              }
              const result = await blobApi.patch('', payload)
              let { error, blob } = result.data.response
              this.statusArchivingCategories = result.data.status
              if (error) this.errorArchivingCategories = error.message
              else {
                const resourceFilesByName = _.transform(
                  resourceFiles,
                  (a, f, i) => {
                    const kOuter = f.resourceId
                    const kInner = f.fileName
                    if (!a[kOuter]) a[kOuter] = {}
                    a[kOuter][kInner] = { ...f, index: i }
                  },
                  {}
                )
                for (const {
                  parentId,
                  fileName,
                  url,
                  expireTs,
                  type: fileType,
                  size
                } of blob) {
                  const id =
                    resourceFilesByName?.[parentId]?.[fileName]?.id ?? null
                  const fIndex =
                    resourceFilesByName?.[parentId]?.[fileName]?.index ?? null
                  if (id != null) {
                    this.fileArchives[id] = {
                      url,
                      expireTs,
                      type: fileType,
                      size
                    }
                    await filesIndexedDb.set(id, {
                      id: id,
                      url,
                      expireTs,
                      type: fileType,
                      size
                    })
                  }
                  if (fIndex != null) {
                    resourceFiles[fIndex] = {
                      ...resourceFiles[fIndex],
                      url,
                      expireTs,
                      type: fileType,
                      size
                    }
                  }
                }
                blob = _.orderBy(
                  _.uniq(
                    _.map(this.editCategory.categories, ({ id }) => {
                      const _fpath = this.getRelativeCategories(
                        id,
                        this.getResourceData.categories,
                        'path'
                      )
                      return `${_fpath}/`
                    })
                  ),
                  'length',
                  'desc'
                )
                const payload = {
                  container: 'resources',
                  blob,
                  deleteParent: true
                }
                const result = await blobApi.delete('', { data: payload })
                const { error } = result.data.response
                this.statusArchivingCategories = result.data.status
                if (error) this.errorArchivingCategories = error.message
              }
              if (this.errorArchivingCategories == null)
                _.forEach(resourceFiles, (f) => {
                  const index = _.findIndex(resourceData, { id: f.resourceId })
                  if (index != -1)
                    resourceData[index].resourceFiles = [
                      ...(resourceData[index]?.resourceFiles || []),
                      f
                    ]
                })
            }
            if (this.errorArchivingCategories == null)
              _.forEach(resourceData, (r) => {
                let index = _.findIndex(this.activeResources, { id: r.id })
                if (index != -1) {
                  this.activeResources[index].categoryId = null
                  this.activeResources[index].resourceCategory = null
                  this.activeResources[index].updateTs = r.updateTs
                  if (
                    _.has(r, 'resourceFiles') &&
                    _.has(this.activeResources[index], 'resourceFiles')
                  )
                    _.forEach(r.resourceFiles, (f) => {
                      const findex = _.findIndex(
                        this.activeResources[index].resourceFiles,
                        { id: f.id }
                      )
                      if (findex != -1) {
                        this.activeResources[index].resourceFiles[
                          findex
                        ].categoryId = null
                        for (const k of [
                          'updateTs',
                          'url',
                          'expireTs',
                          'type',
                          'size'
                        ])
                          this.activeResources[index].resourceFiles[findex][k] =
                            f[k]
                      }
                    })
                } else {
                  index = _.findIndex(this.archivedResources, { id: r.id })
                  if (index != -1) {
                    this.archivedResources[index].categoryId = null
                    this.archivedResources[index].resourceCategory = null
                    this.archivedResources[index].updateTs = r.updateTs
                    if (
                      _.has(r, 'resourceFiles') &&
                      _.has(this.archivedResources[index], 'resourceFiles')
                    )
                      _.forEach(r.resourceFiles, (f) => {
                        const findex = _.findIndex(
                          this.archivedResources[index].resourceFiles,
                          { id: f.id }
                        )
                        if (findex != -1) {
                          this.archivedResources[index].resourceFiles[
                            findex
                          ].categoryId = null
                          for (const k of [
                            'updateTs',
                            'url',
                            'expireTs',
                            'type',
                            'size'
                          ])
                            this.archivedResources[index].resourceFiles[findex][
                              k
                            ] = f[k]
                        }
                      })
                  }
                }
              })
          }
          if (this.errorArchivingCategories == null)
            _.forEach(resourceCategories, (c) =>
              _.remove(this.activeCategories, { id: c.id })
            )
        }
      } catch (e) {
        this.errorArchivingCategories =
          e?.response?.data?.response?.error?.message ||
          e.message ||
          String(e) ||
          JSON.stringify(e)
      }
      if (this.categoryActive.length == 1) {
        if (this.showConfirm && this.editCategory.id == this.categoryActive[0])
          this.categoryActive = ['all']
        else if (
          !this.isArchiveCategoriesAndResources &&
          ids.includes(this.categoryActive[0])
        )
          this.categoryActive = [
            this.editCategory?.resources?.length == 0 ? 'all' : 'uncategorized'
          ]
      }
      this.editCategory = null
      this.isLoadingArchivingCategories = false
      delete this.isActionMultiple.archivingCategories
    },
    async downloadFile(jobId, file, isResume = false) {
      const getFileName = async (folderHandle, baseName) => {
        let name = baseName
        let index = 1
        const existing = new Set()
        for await (const entry of folderHandle.values()) {
          if (entry.kind == 'file') existing.add(entry.name)
        }
        while (existing.has(name)) {
          const [n, ext] = baseName.split(/\.(?=[^.]+$)/)
          name = `${n} (${index})${ext ? '.' + ext : ''}`
          index++
        }
        return name
      }
      const job = this.downloadJobs[jobId]
      if (!isResume && !job.activeFileIds.includes(file.id))
        job.activeFileIds.push(file.id)
      if (!job.isMultipleFiles)
        job.isMultipleFiles = job.activeFileIds.length > 1
      const fileJobId = `${jobId}_${file.id}`
      let fileRecord = null
      if (isResume) fileRecord = await downloadsIndexedDb.get(fileJobId)
      let offset = fileRecord ? fileRecord.offset : 0
      let exportTs = fileRecord ? fileRecord.exportTs : file.exportTs
      let url = fileRecord ? fileRecord.url : file.url
      const fileName = fileRecord ? fileRecord.fileName : file.fileName
      let savedFileName = fileRecord ? fileRecord.fileName : file.fileName
      if (!isResume) {
        savedFileName = await getFileName(job.folderHandle, savedFileName)
        if (_.has(file, 'savedFileName') && file.savedFileName != savedFileName)
          file.savedFileName = savedFileName
        if (
          _.has(fileRecord, 'savedFileName') &&
          fileRecord.savedFileName != savedFileName
        )
          fileRecord.savedFileName = savedFileName
      }
      const size = fileRecord ? fileRecord.size : file.size
      const resourceId = fileRecord ? fileRecord.resourceId : file.resourceId
      const path = fileRecord ? fileRecord.path : file.path

      const fetchOptions = {}
      if (offset > 0) {
        fetchOptions.headers = { Range: `bytes=${offset}-` }
      }

      const handle = await job.folderHandle.getFileHandle(savedFileName, {
        create: true
      })
      const result = await fetch(url, fetchOptions)
      if (!result.ok) job.error = `Failed to fetch ${fileName}`
      else {
        const reader = result.body.getReader()
        while (true) {
          const { done, value } = await reader.read()
          if (done) {
            job.activeFileIds = _.without(job.activeFileIds, file.id)
            await downloadsIndexedDb.remove(fileJobId)
            if (job.isMultipleFiles)
              job.isMultipleFiles = job.activeFileIds.length > 1
            break
          }
          const chunk = value
          const writable = await handle.createWritable({
            keepExistingData: true
          })
          await writable.seek(offset)
          await writable.write(chunk)
          await writable.close()
          job.downloadedBytes += chunk.byteLength
          offset += chunk.byteLength
          job.progress = Math.min(
            Number(((job.downloadedBytes / job.totalSize) * 100).toFixed(2)),
            100
          )
          await downloadsIndexedDb.set(fileJobId, {
            id: file.id,
            resourceId,
            path,
            exportTs,
            url,
            fileName,
            savedFileName,
            size,
            offset
          })
        }
      }
    },
    async downloadingFiles(files, id, source = null) {
      if (
        _.isArray(files) &&
        !_.isEmpty(files) &&
        !_.has(this.downloadIds, id)
      ) {
        const jobId = btoa(`${id}-${Date.now()}`)
          .replace(/\+/g, '-')
          .replace(/\//g, '_')
          .replace(/=+$/, '')
        this.downloadJobs[jobId] = {
          id,
          isMultipleFiles: files.length > 1,
          error: null,
          downloadedBytes: 0,
          progress: 0,
          activeFileIds: []
        }
        this.downloadIds[id] = jobId
        try {
          for (const file of files) {
            if (
              !_.has(file, 'url') ||
              !_.has(file, 'expireTs') ||
              new Date(file.expireTs) < new Date(Date.now() + 3600_000)
            )
              await this.getFileUrl(file, 'attachment')
          }
          if (files.length === 1) {
            const a = document.createElement('a')
            a.href = files[0].url
            a.download = files[0].fileName
            a.rel = 'noopener'
            document.body.appendChild(a)
            a.click()
            document.body.removeChild(a)
            this.downloadJobs[jobId].progress = 100
          } else {
            await this.downloadingFilesZip(files, jobId, id, source)
          }
        } catch (e) {
          this.errorDownloadingFiles =
            e?.response?.data?.response?.error?.message ||
            e.message ||
            String(e) ||
            JSON.stringify(e)
        }
        await downloadsIndexedDb.remove(jobId)
        delete this.downloadJobs[jobId]
        delete this.downloadIds[id]
      }
    },
    async downloadingFilesZip(files, jobId, id, source = null) {
      const zip = new JSZip()
      const totalSize = _.sumBy(files, 'size') || 1
      let downloadedBytes = 0
      await Promise.all(
        _.map(files, async (file) => {
          const response = await fetch(file.url)
          const buffer = await response.arrayBuffer()
          zip.file(file.fileName, buffer)
          downloadedBytes += buffer.byteLength
          this.downloadJobs[jobId].downloadedBytes = downloadedBytes
          this.downloadJobs[jobId].progress = Math.min(
            Number(((downloadedBytes / totalSize) * 100).toFixed(2)),
            99
          )
        })
      )
      const blob = await zip.generateAsync({ type: 'blob' })
      const url = URL.createObjectURL(blob)
      const sanitized = source
        ? source.replace(/\//g, ' - ').replace(/[\\:*?"<>|]/g, '').trim()
        : null
      const zipName = sanitized ? `${sanitized}.zip` : `${id}.zip`
      const a = document.createElement('a')
      a.href = url
      a.download = zipName
      a.rel = 'noopener'
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(url)
      this.downloadJobs[jobId].progress = 100
    },
    async resumeDownloadingFiles() {
      if (
        !_.isEmpty(this.downloadJobs) &&
        !_.isEmpty(this.downloadIds) &&
        _.keys(this.downloadJobs).length == _.keys(this.downloadIds).length
      ) {
        const downloadJob = async (id, jobId) => {
          try {
            if (!_.keys(this.downloadJobs).includes(jobId)) {
              delete this.downloadIds[id]
            } else {
              const job = this.downloadJobs[jobId]
              if (
                job == null ||
                !_.has(job, 'activeFileIds') ||
                !_.isArray(job.activeFileIds) ||
                _.isEmpty(job.activeFileIds)
              ) {
                delete this.downloadJobs[jobId]
                delete this.downloadIds[id]
              } else {
                const jobRecord = await downloadsIndexedDb.get(jobId)
                if (!jobRecord?.handle) {
                  delete this.downloadJobs[jobId]
                  delete this.downloadIds[id]
                } else {
                  const folderHandle = jobRecord.handle
                  const options = { mode: 'readwrite' }
                  let permissionStatus =
                    await folderHandle.queryPermission(options)
                  if (permissionStatus !== 'granted') {
                    permissionStatus =
                      await folderHandle.requestPermission(options)
                    if (permissionStatus !== 'granted')
                      job.error = 'Permission denied to resume download'
                  }
                  if (job?.error != null) {
                    delete this.downloadJobs[jobId]
                    delete this.downloadIds[id]
                  } else {
                    job.error = null
                    job.isMultipleFiles = job.activeFileIds.length > 1
                    job.downloadedBytes = 0
                    job.progress = 0
                    job.folderHandle = folderHandle
                    let files = []
                    let downloadedBytes = 0
                    let totalSize = 0
                    for (const fileId of job.activeFileIds) {
                      const fileJobId = `${jobId}_${fileId}`
                      const fileRecord = await downloadsIndexedDb.get(fileJobId)
                      if (
                        !_.has(fileRecord, 'url') ||
                        !_.has(fileRecord, 'expireTs') ||
                        new Date(fileRecord.expireTs) <
                          new Date(Date.now() + 3600_000)
                      )
                        await this.getFileUrl(fileRecord, 'attachment')
                      files.push(fileRecord)
                      downloadedBytes += fileRecord.offset
                      totalSize += fileRecord.size
                    }
                    job.downloadedBytes = downloadedBytes
                    job.totalSize = totalSize
                    job.files = files
                    await Promise.all(
                      files.map((file) => this.downloadFile(jobId, file, true))
                    )
                    await downloadsIndexedDb.remove(jobId)
                    delete this.downloadJobs[jobId]
                    delete this.downloadIds[id]
                  }
                }
              }
            }
          } catch (error) {
            console.error(error)
            await Promise.all([
              ...this.downloadJobs[jobId].files.map(async (file) =>
                downloadsIndexedDb.remove(`${jobId}-${file.id}`)
              ),
              downloadsIndexedDb.remove(jobId)
            ])
            delete this.downloadJobs[jobId]
            delete this.downloadIds[id]
          }
        }
        await Promise.all(
          Object.entries(this.downloadIds).map(([id, jobId]) =>
            downloadJob(id, jobId)
          )
        )
      }
    },
    handleCardActions(event, item, selectCallback = null) {
      const _setViewResource = (item) => {
        const viewResource = item
        const breadcrumbs = this.getRelativeCategories(
          item.categoryId,
          this.getResourceData.categories,
          'breadcrumbs'
        )
        viewResource.breadcrumbs = breadcrumbs.length > 0 ? breadcrumbs : null
        viewResource.path = this.getRelativeCategories(
          item.categoryId,
          this.getResourceData.categories,
          'path'
        )
        viewResource.index =
          this.getResources[
            this.categoryActive.length == 1 &&
            this.categoryActive[0] == 'archived'
              ? 'archived'
              : 'active'
          ][item.id].index
        this.viewResource = viewResource
      }
      if (Array.isArray(event) && event.length > 0) {
        switch (event[0]) {
          case 'view':
            _setViewResource(item)
            this.showResource = true
            break
          case 'edit':
            if (!this.showResource) _setViewResource(item)
            this.isEdit = true
            break
          case 'archive':
            if (selectCallback) selectCallback(true)
            this.selectedResourceIds = [item.id]
            this.resourceAction = 'archive'
            this.showConfirm = true
            break
          case 'restore':
            if (selectCallback) selectCallback(true)
            this.selectedResourceIds = [item.id]
            this.resourceAction = 'restore'
            this.showConfirm = true
            break
          case 'purge':
            if (selectCallback) selectCallback(true)
            this.selectedResourceIds = [item.id]
            this.resourceAction = 'purge'
            this.showConfirm = true
            break
          case 'assignSimulation':
            if (selectCallback) selectCallback(true)
            if (
              this.simulationResources == null ||
              this.simulationResources.resources == null ||
              !this.simulationResources.resources.includes(item.id)
            ) {
              this.selectedResourceIds = [item.id]
              this.resourceAction = 'assignSimulation'
              this.showConfirm = true
            }
            break
          case 'removeSimulation':
            if (selectCallback) selectCallback(true)
            if (
              this.simulationResources != null &&
              this.simulationResources.resources != null &&
              this.simulationResources.resources.includes(item.id)
            ) {
              this.selectedResourceIds = [item.id]
              this.resourceAction = 'removeSimulation'
              this.showConfirm = true
            }
            break
          case 'move':
            if (selectCallback) selectCallback(true)
            this.selectedResourceIds = [item.id]
            this.setAllCategories(
              this.getResourceData.tree.slice(this.categoriesDivider.at(-1))
            )
            this.resourceAction = 'move'
            this.showCategory = true
            break
          default:
            break
        }
      }
    },
    getFileType(filename) {
      const ext = filename.split('.').pop().toLowerCase()

      if (['pdf'].includes(ext)) return 'pdf'
      if (['mp4', 'mov', 'avi', 'mkv', 'webm'].includes(ext)) return 'video'
      if (['mp3', 'ogg', 'wav', 'flac', 'm4a'].includes(ext)) return 'audio'
      if (['png', 'jpg', 'jpeg', 'gif', 'webp'].includes(ext)) return 'image'
      const office = [
        'doc',
        'docx',
        'docm',
        'dot',
        'dotx',
        'dotm',
        'xls',
        'xlsx',
        'xlsm',
        'xlsb',
        'xlt',
        'xltx',
        'xltm',
        'xlam',
        'ppt',
        'pptx',
        'pptm',
        'pps',
        'ppsx',
        'ppsm',
        'pot',
        'potx',
        'potm',
        'odt',
        'ods',
        'odp'
      ]
      if (office.includes(ext)) return 'office'

      return 'other'
    },
    findCategoryTree(tree, categoryId) {
      return tree.reduce(
        (r, o) =>
          r ||
          (o.id === categoryId
            ? o
            : o.children && this.findCategoryTree(o.children, categoryId)),
        null
      )
    },
    setResourceAction(newValue) {
      this.resourceAction = newValue
    },
    setCategoryAction(newValue) {
      this.categoryAction = newValue
    },
    setCategoryActive(newValue) {
      this.categoryActive = newValue
    },
    setShowCategory(newValue) {
      this.showCategory = newValue
    },
    setShowConfirm(newValue) {
      this.showConfirm = newValue
    },
    setShowResource(newValue) {
      this.showResource = newValue
    },
    setSelectedResourceIds(newValue) {
      this.selectedResourceIds = newValue
    },
    setIsArchiveCategoriesAndResources(newValue) {
      this.isArchiveCategoriesAndResources = newValue
    },
    setAllCategories(newValue) {
      this.allCategories = newValue
    },
    setFetchActiveCategoriesInterval(newValue) {
      if (newValue == null && this.fetchActiveCategoriesInterval != null)
        clearInterval(this.fetchActiveCategoriesInterval)
      this.fetchActiveCategoriesInterval = newValue
    },
    setFetchActiveResourcesInterval(newValue) {
      if (newValue == null && this.fetchActiveResourcesInterval != null)
        clearInterval(this.fetchActiveResourcesInterval)
      this.fetchActiveResourcesInterval = newValue
    },
    setFetchArchivedResourcesInterval(newValue) {
      if (newValue == null && this.fetchArchivedResourcesInterval != null)
        clearInterval(this.fetchArchivedResourcesInterval)
      this.fetchArchivedResourcesInterval = newValue
    },
    setLeftWidth(newValue) {
      this.leftWidth = newValue
    },
    setLeftMinWidth(newValue) {
      this.leftMinWidth = newValue
    },
    setLeftMaxWidth(newValue) {
      this.leftMaxWidth = newValue
    },
    setLeftParentWidth(newValue) {
      this.leftParentWidth = newValue
    },
    getAncestors(id, targetCategories, type, result = []) {
      if (!id || !targetCategories[id]) return result
      const category = targetCategories[id]
      if (category.categoryId) {
        this.getAncestors(category.categoryId, targetCategories, type, result)
      }
      const valueToPush =
        type == 'path'
          ? category.id
          : type == 'breadcrumbs'
            ? category.name
            : category
      result.push(valueToPush)

      return result
    },
    getDescendants(currentId, targetCategories, result = []) {
      if (!result.some((item) => item.id == currentId)) {
        const currentCat = targetCategories[currentId]
        if (currentCat)
          result.push({ id: currentCat.id, name: currentCat.name })
      }
      const children = Object.values(targetCategories).filter(
        (cat) => cat.categoryId == currentId
      )
      for (const child of children)
        this.getDescendants(child.id, targetCategories, result)
      return result
    },
    getRelativeCategories(categoryId, categories, type) {
      if (!categoryId || !categories[categoryId]) {
        return type == 'path' ? '_uncategorized_' : []
      }
      if (['breadcrumbs', 'categories'].includes(type)) {
        const hierarchy = this.getAncestors(categoryId, categories, type)
        return hierarchy
      } else if (type == 'path') {
        const hierarchy = this.getAncestors(categoryId, categories, type)
        return hierarchy.length > 0 ? hierarchy.join('/') : '_uncategorized_'
      } else if (type == 'id') {
        const descendants = this.getDescendants(categoryId, categories)
        return descendants
      }
      return type == 'path' ? '_uncategorized_' : []
    },
    setViewResource(newValue) {
      this.viewResource = newValue
    },
    setCategoriesDivider(newValue) {
      this.categoriesDivider = newValue
    },
    setDownloadJobId(newValue) {
      this.downloadJobId = newValue
    },
    setSimulationResources(newValue) {
      this.simulationResources = newValue
    },
    setUpdateSimulationResources(newValue) {
      this.updateSimulationResources = newValue
    },
    cancelError(key) {
      switch (key) {
        case 'activeCategories':
          this.errorActiveCategories = null
          break
        case 'archivingCategories':
          this.errorArchivingCategories = null
          break
        case 'editingCategory':
          this.errorEditingCategory = null
          break
        case 'creatingCategory':
          this.errorCreatingCategory = null
          break
        case 'activeResources':
          this.errorActiveResources = null
          break
        case 'archivedResources':
          this.errorArchivedResources = null
          break
        case 'archivingResources':
          this.errorArchivingResources = null
          break
        case 'restoringResources':
          this.errorRestoringResources = null
          break
        case 'purgingResources':
          this.errorPurgingResources = null
          break
        case 'movingResources':
          this.errorMovingResources = null
          break
        case 'editingResource':
          this.errorEditingResource = null
          break
        case 'creatingResource':
          this.errorCreatingResource = null
          break
        case 'assignResourcesToSimulation':
          this.errorAssignResourcesToSimulation = null
          break
        case 'removeResourcesFromSimulation':
          this.errorRemoveResourcesFromSimulation = null
          break
        case 'downloadingFiles':
          this.errorDownloadingFiles = null
          break
        default:
          break
      }
    }
  }
})
